import { useState, useCallback, useMemo, memo } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Search, MapPin, DollarSign, Clock, Plus, Loader2, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CreateWantDialog } from "@/components/CreateWantDialog";
import { Navbar } from "@/components/Navbar";
import type { Id } from "../../../convex/_generated/dataModel";

type CategoryRecord = {
  _id: Id<"categories">;
  name: string;
};

// Memoized ad card component
const AdCard = memo(function AdCard({ ad, t }: { ad: any; t: (key: string) => string }) {
  const handleFavoriteClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Toggle favorite logic here
  }, []);

  return (
    <Link href={`/ads/${ad._id}`}>
      <a className="card-elegant overflow-hidden hover:shadow-lg transition-shadow group">
        {/* Image placeholder */}
        <div className="w-full h-48 bg-muted flex items-center justify-center overflow-hidden relative">
          {ad.images && ad.images.length > 0 ? (
            <img
              src={ad.images[0]}
              alt={ad.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              loading="lazy"
            />
          ) : (
            <div className="text-muted-foreground">{t('ad.noImage')}</div>
          )}
          <button 
            className="absolute top-2 right-2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
            title={t('ad.favorites')}
            onClick={handleFavoriteClick}
          >
            <Heart className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-semibold text-lg mb-2 line-clamp-1">{ad.title}</h3>
          
          <div className="space-y-2">
            {ad.price !== undefined && (
              <div className="flex items-center gap-2 text-primary font-bold">
                <DollarSign className="w-4 h-4" />
                <span>${ad.price.toFixed(2)}</span>
              </div>
            )}
            
            {ad.location && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <MapPin className="w-4 h-4" />
                <span className="line-clamp-1">{ad.location}</span>
              </div>
            )}
            
            {ad._creationTime && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Clock className="w-4 h-4" />
                <span>{new Date(ad._creationTime).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      </a>
    </Link>
  );
});

export default function BrowseAds() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  // Convex queries - public access
  const categories = useQuery(api.interactions.getCategories);
  const categoryOptions = useMemo(() => (categories ?? []) as CategoryRecord[], [categories]);
  
  // Traditional Search (Live Filter) - memoize query args
  const searchArgs = useMemo(() => {
    const args: any = {
      limit: 20,
    };
    if (searchQuery && searchQuery.trim().length > 0) {
      args.query = searchQuery.trim();
    }
    if (selectedCategory && selectedCategory !== "all") {
      args.categoryId = selectedCategory as Id<"categories">;
    }
    if (minPrice) {
      args.minPrice = parseFloat(minPrice);
    }
    if (maxPrice) {
      args.maxPrice = parseFloat(maxPrice);
    }
    return args;
  }, [searchQuery, selectedCategory, minPrice, maxPrice]);
  
  const traditionalAds = useQuery(api.ads.searchAds, searchArgs);

  // Semantic Search (On Demand)
  const performSemanticSearch = useAction(api.ads.semanticSearch);
  const [semanticResults, setSemanticResults] = useState<any[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim() && !minPrice && !maxPrice) {
      setSemanticResults(null);
      return;
    }
    
    setIsSearching(true);
    try {
      const results = await performSemanticSearch({
        query: searchQuery,
        categoryId: selectedCategory ? (selectedCategory as Id<"categories">) : undefined,
        minPrice: minPrice ? parseFloat(minPrice) : undefined,
        maxPrice: maxPrice ? parseFloat(maxPrice) : undefined,
      });
      setSemanticResults(results);
    } catch (err) {
      console.error("Semantic search failed", err);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, selectedCategory, minPrice, maxPrice, performSemanticSearch]);

  const handleSearchQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleCategoryChange = useCallback((value: string) => {
    setSelectedCategory(value);
  }, []);

  // If we have semantic results, use them. Otherwise use traditional live search.
  const ads = semanticResults || traditionalAds;
  const isLoading = (ads === undefined && !semanticResults) || isSearching;
  const adList = useMemo(() => (ads ?? []) as any[], [ads]);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <Navbar />

      <div className="container py-8">
        {/* Search and Filter Section */}
        <div className="mb-12">
          <h1 className="mb-8">{t('search.browseListings')}</h1>

          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex gap-4 flex-col md:flex-row">
              <div className="flex-1">
                <Input
                  placeholder={t('search.placeholder')}
                  value={searchQuery}
                  onChange={handleSearchQueryChange}
                  className="h-12"
                />
              </div>
              
              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-full md:w-48 h-12">
                  <SelectValue placeholder={t('search.allCategories')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('search.allCategories')}</SelectItem>
                  {categoryOptions.map((cat) => (
                    <SelectItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button type="submit" size="lg" className="h-12">
                <Search className="w-4 h-4 mr-2" />
                {t('common.search')}
              </Button>
            </div>
            
             <div className="flex gap-4 items-center flex-wrap">
               <span className="text-sm font-medium">{t('search.priceRange')}:</span>
               <div className="flex items-center gap-2">
                 <Input 
                   type="number" 
                   placeholder={t('search.minPrice')} 
                   className="w-32 h-10"
                   min="0"
                   value={minPrice}
                   onChange={(e) => setMinPrice(e.target.value)}
                 />
                 <span className="text-muted-foreground">-</span>
                 <Input 
                   type="number" 
                   placeholder={t('search.maxPrice')} 
                   className="w-32 h-10"
                   min="0"
                   value={maxPrice}
                   onChange={(e) => setMaxPrice(e.target.value)}
                 />
               </div>
            </div>
          </form>
        </div>

        {/* Results */}
        <div>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">{t('common.loading')}</p>
            </div>
          ) : adList.length > 0 ? (
            <>
              <p className="text-muted-foreground mb-6">
                {t('search.resultsFound', { count: adList.length })}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {adList.map((ad) => (
                  <AdCard key={ad._id} ad={ad} t={t} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12 flex flex-col items-center">
              <p className="text-muted-foreground mb-4">{t('search.noResults')}</p>
              
              <Button variant="outline" onClick={() => { setSearchQuery(""); setSemanticResults(null); setMinPrice(""); setMaxPrice(""); }} className="mb-8">
                {t('search.clearSearch')}
              </Button>

              <div className="max-w-md w-full p-6 bg-muted/30 rounded-lg border border-border/50">
                <h3 className="text-lg font-semibold mb-2">{t('wants.createCTA')}</h3>
                <p className="text-sm text-muted-foreground mb-4">{t('wants.createCTADesc')}</p>
                
                <CreateWantDialog 
                  initialQuery={searchQuery}
                  trigger={
                    <Button variant="secondary" className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('wants.createButton')}
                    </Button>
                  }
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
